import { createContext, useContext, useMemo } from 'react';

import { CampItemResponse } from '../../../api/camps';
import { Kid } from '../../../components/KidsInput';
import { useCurrentCampSelection } from '../../../hooks/camps/useCurrentCampSelection';
import { getAgeInMonths } from '../../../utils/date';
import { CheckoutKid, useCheckoutKidsInfo } from '../../state/checkoutKids';

const CampsAPIContext = createContext(false);

export const useCampsAPI = () => {
  return useContext(CampsAPIContext);
};

export const CampsAPIProvider = ({
  children,
  locationId,
  locationNameId,
  enabled,
}: {
  children: React.ReactNode;
  locationId: number;
  locationNameId: string;
  enabled: boolean;
}) => {
  if (!enabled) return children;

  return <CampsAPIFetcher locationId={locationId}>{children}</CampsAPIFetcher>;
};

const KidsWithEligibilityContext = createContext<CheckoutKid[] | null>(null);
export const useKidsWithEligibility = () => {
  const ctx = useContext(KidsWithEligibilityContext);
  if (!ctx) {
    throw new Error('useKidsWithEligibility used outside of CampsAPIProvider');
  }
  return ctx;
};

const AvailableCampsContext = createContext<CampItemResponse[]>([]);
export const useAvailableCamps = () => useContext(AvailableCampsContext);

const CurrentCampContext = createContext<CampItemResponse | null>(null);
export const useCurrentCamp = () => useContext(CurrentCampContext);

const SetSelectedCampIdContext = createContext<(campId: number) => void>(
  () => {}
);
export const useSetSelectedCampId = () => useContext(SetSelectedCampIdContext);

function CampsAPIFetcher({
  locationId,
  children,
}: {
  locationId: number;
  children: React.ReactNode;
}) {
  const { data, currentCampDetails, setSelectedCampId } =
    useCurrentCampSelection(locationId);

  const kids = useCheckoutKidsInfo();
  const kidsWithEligibility = useMemo(
    () => kids.map((kid) => setCampEligibility(kid, currentCampDetails)),
    [kids, currentCampDetails]
  );

  return (
    <CampsAPIContext.Provider value={true}>
      <KidsWithEligibilityContext.Provider value={kidsWithEligibility}>
        <AvailableCampsContext.Provider value={data ?? []}>
          <CurrentCampContext.Provider value={currentCampDetails ?? null}>
            <SetSelectedCampIdContext.Provider value={setSelectedCampId}>
              {children}
            </SetSelectedCampIdContext.Provider>
          </CurrentCampContext.Provider>
        </AvailableCampsContext.Provider>
      </KidsWithEligibilityContext.Provider>
    </CampsAPIContext.Provider>
  );
}

export function setCampEligibility(kid: Kid, camp?: CampItemResponse): Kid {
  if (!camp) return kid;

  const ageOnCampDate = getAgeInMonths(
    kid.birthdate,
    new Date(camp.start_date)
  );

  const isEligible =
    ageOnCampDate >= camp.age_from && ageOnCampDate <= camp.age_to;
  return {
    ...kid,
    isEligible,
  };
}
